import { watch } from "vue";
import { useStore } from "@/core/services/store";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import { getDataOptions } from "@/components/Tools/FormHelper/Helper/functions";

/**
 * eBay-spezifisch: alle Felder, die ihre Optionen aus
 * App\Config\Ebay\ShippingCarrierOptions beziehen, hängen von den gewählten
 * Marketplaces (salesOrder.updateStrategy.marketplaces) ab. Ohne
 * optionsParams.marketplaces liefert die Options-Klasse eine leere Liste
 * zurück, das Feld bliebe also leer.
 */
const CARRIER_FIELDS = [
  {
    form: "app.salesOrder.shipping",
    group: "assignmentTableId",
    field: "key"
  },
  {
    form: "app.tracking.carrierAssignment",
    group: "carrierAssignmentTableId",
    field: "value"
  }
];

function findCarrierField(store, target) {
  const form = store.getters.form(target.form);
  const groupField = form?.find?.(f => f.name === target.group);

  return groupField?.fields?.find(f => f.name === target.field);
}

/**
 * Ändert sich die Marketplace-Auswahl, wird field.optionsParameters auf jedem
 * Carrier-Feld gesetzt und ein Reload getriggert. Das Setzen am Feld stellt
 * sicher, dass FormHelper-internes Laden (Group.vue created, Reload-Button via
 * mixins.js) dieselben Parameter verwendet, also auch beim manuellen Reload die
 * marketplaces mitgeschickt werden.
 *
 * DataOptions-Cache (memoize per JSON.stringify des Payloads) speichert pro
 * Marketplace-Kombi eine eigene Antwort, also kein Refetch bei Wechsel zurück.
 * marketplaces wird sortiert, damit reihenfolgeunterschiedliche aber inhaltlich
 * gleiche Auswahlen denselben Cache-Key treffen.
 */
export function useEbayCarrierOptions() {
  const store = useStore();
  const { selectedIntegration } = useIntegration();

  if (store.getters.selectedApp?.name !== "ebay") return;

  return watch(
    () =>
      selectedIntegration.value?.salesOrder?.updateStrategy?.value
        ?.marketplaces,
    marketplaces => {
      if (!Array.isArray(marketplaces)) return;

      const sortedMarketplaces = [...marketplaces].sort();

      CARRIER_FIELDS.forEach(target => {
        const field = findCarrierField(store, target);
        if (!field?.optionsClass) return;

        field.optionsParameters = {
          optionsParams: { marketplaces: sortedMarketplaces }
        };

        field.loading = true;
        getDataOptions(field, {
          optionsClass: field.optionsClass,
          optionsMethod: field.optionsMethod,
          ...field.optionsParameters
        });
      });
    },
    { immediate: true, deep: true }
  );
}
